import React from "react";

import ROUTES from "manager/config/routes";
import { useTogglePropertyVeroGo } from "manager/hooks/api/propertyQueries";
import useModal from "shared/hooks/useModal";

import {
  GreenSwitch,
  SettingsLink,
  openVeroGoOverrideDialog,
} from "./shared";

type PropertyRowProps = {
  property: any;
  portfolioName: string;
  portfolioActive: boolean;
  isLocked: boolean;
  onAfterToggle?: (newValue: boolean) => void;
  onStartMutation?: (itemType?: string, itemId?: number) => void;
  flash?: boolean;
};

const PropertyRow = ({
  property,
  portfolioName,
  portfolioActive,
  isLocked,
  onAfterToggle,
  onStartMutation,
  flash,
}: PropertyRowProps) => {
  const { togglePropertyVeroGo, isTogglePropertyVeroGoLoading } =
    useTogglePropertyVeroGo({ propertyId: property.id });
  const { openModalDialog } = useModal();

  const isActive = !!property.veroGoActive;
  const hasMixedDealSetupInheritance =
    Boolean(property.isDealSetupFromPortfolio) !==
    Boolean(property.isPrescreeningDealSetupFromPortfolio);
  const isDisabled = isTogglePropertyVeroGoLoading || isLocked;

  const handleToggle = async (checked: boolean) => {
    if (checked && hasMixedDealSetupInheritance) {
      const result = await openVeroGoOverrideDialog(openModalDialog, {
        title: "Enable Vero Go for this property?",
        subtitle: `${property.name} in ${portfolioName}`,
        nowState: isActive ? "on" : "off",
        nowRelationship: "inherited",
        afterState: "on",
        afterRelationship: "custom",
        infoText: (
          <>
            This property currently has mixed inheritance between screening and
            pre-screening settings. Enabling Vero Go will preserve existing
            custom settings and create a property copy for the inherited side so
            both remain managed together for this property.
          </>
        ),
        primaryButtonLabel: "Enable Vero Go",
        secondaryButtonLabel: "Cancel",
        primaryValue: "confirm",
        secondaryValue: "cancel",
      });

      if (result !== "confirm") return;
    }

    // Show decouple confirmation whenever an inheriting property diverges
    // from its portfolio setting (both directions).
    const needsDecoupleConfirmation = checked !== portfolioActive;

    if (needsDecoupleConfirmation) {
      const result = await openVeroGoOverrideDialog(openModalDialog, {
        title: "Override portfolio setting?",
        subtitle: `${property.name} in ${portfolioName}`,
        nowState: portfolioActive ? "on" : "off",
        nowRelationship: "inherited",
        afterState: checked ? "on" : "off",
        afterRelationship: "custom",
        infoText: (
          <>
            This will <strong>separate</strong> {property.name} from{" "}
            {portfolioName}. Its screening and pre-screening settings will no
            longer stay in sync with the portfolio.
          </>
        ),
        primaryButtonLabel: `Override to ${checked ? "On" : "Off"}`,
        secondaryButtonLabel: "Keep inherited",
        primaryValue: "confirm",
        secondaryValue: "cancel",
      });

      if (result !== "confirm") return;
    }

    onStartMutation?.("property", property.id);
    togglePropertyVeroGo(checked, {
      onSettled: () => onAfterToggle?.(checked),
    });
  };

  return (
    <div
      data-vero-go-item={`property-${property.id}`}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 20px 10px 48px",
        borderBottom: "1px solid #f0f0f0",
      }}
      className={flash ? "vero-go-flash" : undefined}
    >
      <div style={{ flex: 1 }}>
        <div>
          <SettingsLink
            to={ROUTES.property.replace(":id", String(property.id))}
            style={{ fontWeight: 500, fontSize: 13 }}
          >
            {property.name}
          </SettingsLink>
          {property.address && (
            <span style={{ color: "#999", marginLeft: 8, fontSize: 12 }}>
              {property.address}
              {property.city ? `, ${property.city}` : ""}
              {property.state ? `, ${property.state}` : ""}
            </span>
          )}
        </div>
      </div>
      <GreenSwitch
        checked={isActive}
        loading={isTogglePropertyVeroGoLoading}
        onChange={handleToggle}
        disabled={isDisabled}
        size="small"
      />
    </div>
  );
};

export default PropertyRow;
